import Image from 'next/image';

import type { OrderDetailsOrderLine } from '@/types/orderDetailsOrder';
import { getOrderLineUnitPriceWithoutDiscount } from '@/utils/order';
import type { WithRequired } from '@/utils/tsutils';

import { Box } from '@/components/Box';
import { Complimentary } from '@/components/Complimentary/Complimentary';
import { TaxedPrice } from '@/components/TaxedPrice';
import { Typography } from '@/components/Typography';
import type { OrderLineFragment } from '@/graphql/saleor/api';
import { ProductType, VOUCHER_TYPE } from '@/lib/const';
import type { getEngravingDetailsFromMetadata } from '@/lib/engraving';
import { OrderLineWithdrawalCaption } from '@/views/withdrawal/components/OrderLineWithdrawalCaption';
import { getOrderLineWithdrawalCaptionModel } from '@/views/withdrawal/orderLineWithdrawalCaption';

import { OrderLineReturnInfo } from '../components/OrderLineReturnInfo';
import { useOrderDetailsContext } from '../OrderDetailsContext';
import type { OrderDetailsContextValue } from '../orderDetailsContext.types';
import { ProductName } from '../OrderDetailsView';
import { EngravingOrderLineRow } from './EngravingOrderLineRow';

import * as S from '../OrderDetailsView.styles';

export type OrderLineRowProps = Readonly<{
  cancelReturnComment: string;
  channelSlug: OrderDetailsContextValue['channelSlug'];
  engravingDetails: ReturnType<typeof getEngravingDetailsFromMetadata>;
  formatMessage: OrderDetailsContextValue['formatMessage'];
  fulfillmentId: string;
  hasFulfillment: OrderDetailsContextValue['hasFulfillment'];
  imageLoader: OrderDetailsContextValue['imageLoader'];
  index: number;
  isMobileView: OrderDetailsContextValue['isMobileView'];
  isOrderHistory: boolean;
  isWithdrawalEligibilityLoading: boolean;
  languageCode: OrderDetailsContextValue['languageCode'];
  lineAlreadyInReturn: boolean;
  orderChannelSlug: string;
  orderId: string;
  orderLine: OrderDetailsOrderLine;
  productName: string | undefined;
  productType: ProductType | undefined;
  returnStatus: string;
  showLineReturnStatus: boolean;
  taxMessage: OrderDetailsContextValue['taxMessage'];
  thumbnail: { url: string } | null | undefined;
  voucherType: OrderDetailsContextValue['voucherType'];
  withdrawalEligibility: OrderDetailsContextValue['withdrawal']['withdrawalEligibility'];
}>;

type OrderLinePriceSectionProps = Readonly<{
  formatMessage: OrderDetailsContextValue['formatMessage'];
  index: number;
  orderChannelSlug: string;
  orderLine: OrderDetailsOrderLine;
  taxMessage: OrderDetailsContextValue['taxMessage'];
  voucherType: OrderDetailsContextValue['voucherType'];
}>;

function OrderLinePriceSection({
  formatMessage,
  index,
  orderChannelSlug,
  orderLine,
  taxMessage,
  voucherType,
}: OrderLinePriceSectionProps) {
  const isComplimentary = ['gift', 'engraving'].includes(
    orderLine?.variant?.product.productType.name.toLowerCase() ?? '',
  );
  const showVatMessage = voucherType === VOUCHER_TYPE.PERCENTAGE;

  if (isComplimentary) {
    return <Complimentary />;
  }

  return (
    <>
      <TaxedPrice
        channel={orderChannelSlug}
        data-test-id={`OrderDetailsViewOrderLineTotalPrice-${index}`}
        displayZero={!showVatMessage}
        justifyContent='right'
        price={getOrderLineUnitPriceWithoutDiscount(
          orderLine as WithRequired<Partial<OrderLineFragment>, 'totalPrice'>,
          voucherType,
        )}
      />
      {showVatMessage ? null : (
        <Typography
          color='secondaryGrey'
          data-test-id={`OrderDetailsViewOrderLineVATMessage-${index}`}
          scale='p3'
          textAlign='right'
        >
          {taxMessage ? formatMessage(taxMessage) : null}
        </Typography>
      )}
    </>
  );
}

export function OrderLineRow(props: OrderLineRowProps) {
  const {
    cancelReturnComment,
    channelSlug,
    engravingDetails,
    formatMessage,
    fulfillmentId,
    hasFulfillment,
    imageLoader,
    index,
    isMobileView,
    isOrderHistory,
    isWithdrawalEligibilityLoading,
    languageCode,
    lineAlreadyInReturn,
    orderChannelSlug,
    orderId,
    orderLine,
    productName,
    productType,
    returnStatus,
    showLineReturnStatus,
    taxMessage,
    thumbnail,
    voucherType,
    withdrawalEligibility,
  } = props;

  const { guestSession } = useOrderDetailsContext();
  const onGuestViewReturnDetails = guestSession?.onGuestViewReturnDetails;

  if (
    productType === ProductType.Engraving &&
    engravingDetails &&
    orderLine.variant
  ) {
    return (
      <EngravingOrderLineRow
        engravingDetails={engravingDetails}
        hasFulfillment={hasFulfillment}
        index={index}
        isOrderHistory={isOrderHistory}
        isWithdrawalEligibilityLoading={isWithdrawalEligibilityLoading}
        lineAlreadyInReturn={lineAlreadyInReturn}
        orderLine={orderLine}
        withdrawalEligibility={withdrawalEligibility}
      />
    );
  }

  const showWithdrawalCaption =
    isOrderHistory && !hasFulfillment && !lineAlreadyInReturn;
  const showSku = productType !== ProductType.Engraving;

  return (
    <S.OrderLineRow
      key={orderLine.id}
      data-testid={`OrderDetailsViewOrderLineRow-${index}`}
      justifyContent='space-between'
    >
      <S.OrderProductImageInfo>
        <S.ImageWrapper>
          {thumbnail ? (
            <Image
              alt={productName ?? ''}
              data-test-id={`OrderDetailsViewOrderLineImage-${index}`}
              fill
              loader={imageLoader}
              src={thumbnail.url}
              style={{
                objectFit: 'contain',
              }}
            />
          ) : null}
        </S.ImageWrapper>
        <S.StyledGrid>
          <ProductName
            channel={channelSlug}
            id={orderLine?.variant?.product?.id}
            index={index}
            languageCode={languageCode}
            productName={productName ?? ''}
            productType={orderLine?.variant?.product?.productType?.slug}
          />
          {showSku ? (
            <Typography color='secondaryGrey' scale='p3'>
              {orderLine.variant?.sku}
            </Typography>
          ) : null}
          {showWithdrawalCaption ? (
            <OrderLineWithdrawalCaption
              model={getOrderLineWithdrawalCaptionModel(
                orderLine.id,
                withdrawalEligibility,
                isWithdrawalEligibilityLoading,
              )}
            />
          ) : null}
          {showLineReturnStatus && isMobileView ? (
            <Box my={1}>
              <OrderLineReturnInfo
                cancelReturnComment={cancelReturnComment}
                fulfillmentId={fulfillmentId}
                onGuestViewReturnDetails={onGuestViewReturnDetails}
                orderId={orderId}
                returnStatus={returnStatus}
                showReturnDetailsLink={isOrderHistory}
              />
            </Box>
          ) : null}
        </S.StyledGrid>
      </S.OrderProductImageInfo>
      {showLineReturnStatus && !isMobileView ? (
        <S.OrderProductReturnInfo>
          <S.StyledGrid>
            <OrderLineReturnInfo
              cancelReturnComment={cancelReturnComment}
              fulfillmentId={fulfillmentId}
              onGuestViewReturnDetails={onGuestViewReturnDetails}
              orderId={orderId}
              returnStatus={returnStatus}
              showReturnDetailsLink={isOrderHistory}
            />
          </S.StyledGrid>
        </S.OrderProductReturnInfo>
      ) : null}
      <S.PriceWrap>
        <OrderLinePriceSection
          formatMessage={formatMessage}
          index={index}
          orderChannelSlug={orderChannelSlug}
          orderLine={orderLine}
          taxMessage={taxMessage}
          voucherType={voucherType}
        />
      </S.PriceWrap>
    </S.OrderLineRow>
  );
}
